import React, { useState, useEffect } from 'react';
import { getRefiHistory, executeRefiAccounting, triggerRefiAccounting, deleteRefi } from '../services/api';
import { format } from 'date-fns';

const DailyActivitySummary = ({ onUpdate }) => {
  const [todayRefis, setTodayRefis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRefi, setEditingRefi] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadTodayRefis();
  }, []);

  const loadTodayRefis = async () => {
    try {
      setLoading(true);
      const response = await getRefiHistory({ days: 1 });
      setTodayRefis(response.data || []);
    } catch (error) {
      console.error('Error loading today\'s refis:', error);
      alert('Failed to load today\'s refis');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending_approval: { class: 'badge badge-pending', text: '⏳ Pending Approval' },
      executed: { class: 'badge badge-success', text: '✅ Executed' },
      failed: { class: 'badge badge-error', text: '❌ Failed' },
      execution_failed: { class: 'badge badge-error', text: '❌ Execution Failed' },
    };
    const badge = statusMap[status] || { class: 'badge', text: status || 'N/A' };
    return <span className={badge.class}>{badge.text}</span>;
  };

  const getExecutionStatusBadge = (refi) => {
    const executionStatus = refi.execution_status;
    const status = refi.status;
    
    // Special case: if status is "failed", show that regardless of execution_status
    if (status === 'failed') {
      return <span className="badge badge-error">❌ Failed</span>;
    }
    
    // Special case: if status is "executed" but execution_status is "pending_approval",
    // this means it ran in production mode (not via manual approval workflow)
    if (status === 'executed' && executionStatus === 'pending_approval') {
      return <span className="badge badge-success">✅ Executed (Production)</span>;
    }
    
    const statusMap = {
      pending_approval: { class: 'badge badge-pending', text: '⏳ Awaiting Approval' },
      approved_executing: { class: 'badge badge-pending', text: '🔄 Executing...' },
      executed: { class: 'badge badge-success', text: '✅ Executed' },
      execution_failed: { class: 'badge badge-error', text: '❌ Execution Failed' },
    };
    
    const badge = statusMap[executionStatus] || { class: 'badge', text: 'N/A' };
    return <span className={badge.class}>{badge.text}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const isCombinedRefi = (refi) => {
    const loanCount = parseInt(refi.new_loan_count) || 0;
    return refi.is_combined_refi === true && loanCount > 1;
  };

  const getNewLoansDisplay = (refi) => {
    if (isCombinedRefi(refi) && refi.new_loans && Array.isArray(refi.new_loans) && refi.new_loans.length > 0) {
      return refi.new_loans.map(loan => loan.loan_number).join(', ');
    }
    return refi.new_loan_display_id || refi.new_loan_id;
  };

  const getRefiPayoffDisplay = (refi) => {
    if (isCombinedRefi(refi) && refi.combined_payoff) {
      return formatCurrency(refi.combined_payoff);
    }
    return formatCurrency(refi.refi_payoff);
  };

  const handleEdit = (refi) => {
    setEditingRefi(refi);
    setEditForm({
      new_loan_id: refi.new_loan_id || refi.new_loan_display_id,
      old_loan_number: refi.old_loan_display_id || refi.old_loan_number || refi.old_loan_id,
      refi_payoff: refi.refi_payoff,
      refi_party: refi.refi_party || 'Panacea Fund I, LLC',
    });
  };

  const handleCancelEdit = () => {
    setEditingRefi(null);
    setEditForm({});
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleRetrigger = async () => {
    if (!editForm.new_loan_id || !editForm.old_loan_number || !editForm.refi_payoff) {
      alert('Please fill in all required fields');
      return;
    }

    const confirmMsg = `Re-trigger refi accounting with updated data?\n\n` +
                       `Old Loan: ${editForm.old_loan_number}\n` +
                       `New Loan: ${editForm.new_loan_id}\n` +
                       `Payoff: ${formatCurrency(editForm.refi_payoff)}`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      console.log('🔄 Re-triggering refi with updated data:', editForm);
      await triggerRefiAccounting({
        new_loan_id: parseInt(editForm.new_loan_id),
        old_loan_number: editForm.old_loan_number,
        refi_payoff: parseFloat(editForm.refi_payoff),
        refi_party: editForm.refi_party,
      });
      
      alert('✅ Refi re-triggered successfully! It will run in preview mode. Refresh in a few seconds to see the updated entry.');
      
      handleCancelEdit();
      setTimeout(() => {
        loadTodayRefis();
        if (onUpdate) onUpdate();
      }, 2000);
    } catch (error) {
      console.error('❌ Error re-triggering refi:', error);
      alert(`Failed to re-trigger refi:\n\n${error.response?.data?.error || error.message}`);
    }
  };

  const handleExecute = async (refi) => {
    if (!refi.s3_summary_key) {
      alert('No S3 key found for this refi. Cannot execute.');
      return;
    }

    const newLoanDisplay = getNewLoansDisplay(refi);
    const payoffDisplay = isCombinedRefi(refi)
      ? `Combined Payoff: ${formatCurrency(refi.combined_payoff)}`
      : `Refi Payoff: ${formatCurrency(refi.refi_payoff)}`;

    const confirmMsg = `Execute refi accounting for ${refi.old_loan_display_id || refi.old_loan_number} → ${newLoanDisplay}?\n\n` +
                       `${payoffDisplay}\n\n` +
                       `This will apply all accounting changes to LoanPro.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      console.log('🚀 Executing refi:', refi);
      const response = await executeRefiAccounting(refi.s3_summary_key);
      console.log('✅ Execution response:', response);
      
      alert(`✅ Refi execution triggered successfully!\n\nRequest ID: ${response.request_id}\n\nThe refi will be processed in the background. Refresh in a few seconds to see the updated status.`);
      
      setTimeout(() => {
        loadTodayRefis();
        if (onUpdate) onUpdate();
      }, 2000);
    } catch (error) {
      console.error('❌ Error executing refi:', error);
      alert(`Failed to execute refi accounting:\n\n${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (refi) => {
    if (!refi.s3_summary_key) {
      alert('No S3 key found for this refi. Cannot delete.');
      return;
    }

    const newLoanDisplay = getNewLoansDisplay(refi);
    const confirmMsg = `Delete this pending refi record?\n\n` +
                       `Old Loan: ${refi.old_loan_display_id || refi.old_loan_id}\n` +
                       `New Loan: ${newLoanDisplay}\n\n` +
                       `This removes the record from the dashboard. It does NOT reverse any changes in LoanPro.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      console.log('🗑️ Deleting refi:', refi);
      await deleteRefi(refi.s3_summary_key);
      alert('✅ Refi record deleted successfully.');
      setTimeout(() => {
        loadTodayRefis();
        if (onUpdate) onUpdate();
      }, 500);
    } catch (error) {
      console.error('❌ Error deleting refi:', error);
      alert(`Failed to delete refi record:\n\n${error.response?.data?.error || error.message}`);
    }
  };

  const getActionButtons = (refi) => {
    const buttons = [];

    // Don't show any buttons for refis that are already fully executed
    // Either via manual Execute button OR ran in production mode
    if (refi.execution_status === 'executed' || refi.status === 'executed') {
      return buttons;
    }

    // Check if this is a combined refi
    const isCombined = isCombinedRefi(refi);

    // For failed refis - show Edit + Delete buttons (Edit only for single refis)
    if (refi.status === 'failed' || refi.execution_status === 'execution_failed') {
      if (!isCombined) {
        buttons.push(
          <button
            key="edit"
            className="button button-secondary"
            style={styles.actionButton}
            onClick={() => handleEdit(refi)}
          >
            ✏️ Edit
          </button>
        );
      }
      buttons.push(
        <button
          key="delete"
          className="button"
          style={{ ...styles.actionButton, backgroundColor: '#dc3545', color: 'white', border: 'none' }}
          onClick={() => handleDelete(refi)}
        >
          🗑️ Delete
        </button>
      );
      return buttons;
    }

    // For pending approvals that haven't been executed yet
    // Show both Edit and Execute buttons
    if (refi.execution_status === 'pending_approval' || 
        refi.execution_status === 'approved_executing') {
      
      // Edit button (only for single refis - combined refis can't be edited)
      if (!isCombined) {
        buttons.push(
          <button
            key="edit"
            className="button button-secondary"
            style={styles.actionButton}
            onClick={() => handleEdit(refi)}
          >
            ✏️ Edit
          </button>
        );
      }

      // Execute button (but not if currently executing)
      if (refi.execution_status !== 'approved_executing') {
        buttons.push(
          <button
            key="execute"
            className="button button-success"
            style={{ ...styles.actionButton, backgroundColor: '#28a745' }}
            onClick={() => handleExecute(refi)}
          >
            🚀 Execute
          </button>
        );
      }

      // Delete button — always available for pending refis
      buttons.push(
        <button
          key="delete"
          className="button"
          style={{ ...styles.actionButton, backgroundColor: '#dc3545', color: 'white', border: 'none' }}
          onClick={() => handleDelete(refi)}
        >
          🗑️ Delete
        </button>
      );
    }

    return buttons;
  };

  const todayStats = {
    total: todayRefis.length,
    // Processed = completed successfully (either preview or production)
    success: todayRefis.filter(r => r.status === 'success' || r.status === 'executed').length,
    failed: todayRefis.filter(r => r.status === 'failed').length,
    // Awaiting Approval = pending AND not already executed in production
    pending: todayRefis.filter(r => 
      r.execution_status === 'pending_approval' && r.status !== 'executed'
    ).length,
    // Executed = manually executed OR ran in production mode
    executed: todayRefis.filter(r => 
      r.execution_status === 'executed' || 
      (r.status === 'executed' && r.execution_status === 'pending_approval')
    ).length,
  };

  return (
    <div className="card">
      <div style={styles.header}>
        <h2>📊 Daily Activity Summary - {format(new Date(), 'MMMM dd, yyyy')}</h2>
        <button className="button button-secondary" onClick={loadTodayRefis}>
          🔄 Refresh
        </button>
      </div>

      {/* Today's Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Total Today</div>
          <div style={styles.statValue}>{todayStats.total}</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Processed</div>
          <div style={{ ...styles.statValue, color: '#28a745' }}>{todayStats.success}</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Failed</div>
          <div style={{ ...styles.statValue, color: '#dc3545' }}>{todayStats.failed}</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Awaiting Approval</div>
          <div style={{ ...styles.statValue, color: '#ffc107' }}>{todayStats.pending}</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Executed</div>
          <div style={{ ...styles.statValue, color: '#17a2b8' }}>{todayStats.executed}</div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingRefi && (
        <div style={styles.modal} onClick={handleCancelEdit}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>✏️ Edit & Re-trigger Refi</h3>
              <button className="button button-secondary" onClick={handleCancelEdit}>
                Close
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  New Loan ID *
                  <input
                    type="number"
                    value={editForm.new_loan_id || ''}
                    onChange={(e) => handleFormChange('new_loan_id', e.target.value)}
                    style={styles.input}
                    placeholder="e.g., 25930"
                  />
                </label>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Old Loan Number *
                  <input
                    type="text"
                    value={editForm.old_loan_number || ''}
                    onChange={(e) => handleFormChange('old_loan_number', e.target.value)}
                    style={styles.input}
                    placeholder="e.g., 2330TEST or 912508000103"
                  />
                </label>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Refi Payoff Amount *
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.refi_payoff || ''}
                    onChange={(e) => handleFormChange('refi_payoff', e.target.value)}
                    style={styles.input}
                    placeholder="e.g., 125000.00"
                  />
                </label>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Refi Party *
                  <select
                    value={editForm.refi_party || ''}
                    onChange={(e) => handleFormChange('refi_party', e.target.value)}
                    style={styles.input}
                  >
                    <option value="Panacea Fund I, LLC">Panacea Fund I, LLC</option>
                    <option value="Panacea Fund II, LLC">Panacea Fund II, LLC</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
              </div>

              <div className="alert-warning" style={{ padding: '15px', marginBottom: '20px', fontSize: '14px' }}>
                <strong>⚠️ Note:</strong> This will re-trigger the refi accounting in preview mode.
                The system will generate new CSVs with the updated data. Review them before executing.
              </div>

              <div style={styles.modalActions}>
                <button
                  className="button button-secondary"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  className="button button-primary"
                  onClick={handleRetrigger}
                  style={{ backgroundColor: '#007bff' }}
                >
                  🔄 Re-trigger Refi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's Refis Table */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : todayRefis.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No refis processed today yet</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Old Loan</th>
                <th>New Loan</th>
                <th>Refi Payoff</th>
                <th>Adjustment</th>
                <th>Status</th>
                <th>Execution</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {todayRefis.map((refi, index) => (
                <tr key={index} className={refi.status === 'failed' ? 'row-failed' : ''}>
                  <td>{formatDate(refi.timestamp || refi.processed_at)}</td>
                  <td>
                    <div style={styles.loanCell}>
                      {refi.old_loan_display_id || refi.old_loan_number || refi.old_loan_id}
                      {refi.status === 'failed' && (
                        <span style={styles.failedIndicator}>❌</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {getNewLoansDisplay(refi)}
                    {isCombinedRefi(refi) && (
                      <div style={{fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px'}}>
                        ({refi.new_loan_count} loans)
                      </div>
                    )}
                  </td>
                  <td>{getRefiPayoffDisplay(refi)}</td>
                  <td>
                    <span style={styles.adjustmentType}>
                      {refi.adjustment_type || 'N/A'}
                    </span>
                    {' '}
                    {formatCurrency(refi.adjustment_amount || 0)}
                  </td>
                  <td>{getStatusBadge(refi.status)}</td>
                  <td>{getExecutionStatusBadge(refi)}</td>
                  <td>
                    <div style={styles.actionsCell}>
                      {getActionButtons(refi)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="alert-info" style={{ marginTop: '20px', padding: '15px', borderRadius: '6px' }}>
        <p style={{ margin: 0, fontSize: '14px' }}>
          💡 <strong>Tip:</strong> Click "Edit" on failed refis to correct data and re-trigger. 
          Click "Execute" on pending refis to apply changes to LoanPro.
        </p>
      </div>
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px',
    marginBottom: '25px',
    padding: '15px',
    backgroundColor: 'var(--card-bg-hover)',
    borderRadius: '8px',
  },
  statBox: {
    textAlign: 'center',
    padding: '10px',
  },
  statLabel: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    marginBottom: '5px',
    fontWeight: '500',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--text-primary)',
  },
  tableContainer: {
    overflowX: 'auto',
    marginBottom: '20px',
  },
  loanCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  failedIndicator: {
    fontSize: '12px',
  },
  adjustmentType: {
    fontWeight: '600',
    color: 'var(--text-muted)',
  },
  actionsCell: {
    display: 'flex',
    gap: '5px',
    flexWrap: 'wrap',
  },
  actionButton: {
    padding: '6px 12px',
    fontSize: '12px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted)',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--modal-overlay)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'var(--modal-bg)',
    borderRadius: '8px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    padding: '20px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '20px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    marginTop: '5px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-primary)',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
};

export default DailyActivitySummary;

